import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  BuildingOfficeIcon, 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  GlobeAltIcon,
  CurrencyDollarIcon,
  LanguageIcon,
  ClockIcon,
  PhotoIcon,
  CloudArrowUpIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const GeneralSettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [formData, setFormData] = useState({
    gymName: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    currency: 'DA',
    language: 'fr',
    timezone: 'Africa/Algiers',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (settings) {
      setFormData({
        gymName: settings.gymName || '',
        address: settings.address || '',
        phone: settings.phone || '',
        email: settings.email || '',
        website: settings.website || '',
        currency: settings.currency || 'DA',
        language: settings.language || 'fr',
        timezone: settings.timezone || 'Africa/Algiers',
        logo: settings.logo || null
      });
      // Set logo preview if logo exists
      if (settings.logo) {
        setLogoPreview(settings.logo);
      }
    }
  }, [settings]);

  const handleChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Safe call to onSettingsChange if it exists
    if (typeof onSettingsChange === 'function') {
      onSettingsChange({ ...settings, ...newFormData });
    }
    
    // Safe call to onHasChanges if it exists
    if (typeof onHasChanges === 'function') {
      onHasChanges(true);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setUploadStatus('error');
      toast.error('Veuillez sélectionner un fichier image (PNG, JPG, etc.)');
      setTimeout(() => setUploadStatus('idle'), 3000);
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadStatus('error');
      toast.error('Le fichier est trop volumineux (max. 5MB)');
      setTimeout(() => setUploadStatus('idle'), 3000);
      return;
    }

    setUploadStatus('uploading');

    const reader = new FileReader();
    reader.onload = (e) => {
      const logoData = e.target.result;
      setLogoPreview(logoData);
      handleChange('logo', logoData);
      setUploadStatus('success');
      toast.success('Logo téléchargé avec succès!');
      setTimeout(() => setUploadStatus('idle'), 3000);
    };

    reader.onerror = () => {
      setUploadStatus('error');
      toast.error('Erreur lors du téléchargement du fichier');
      setTimeout(() => setUploadStatus('idle'), 3000);
    };

    reader.readAsDataURL(file);
  };

  // Remove logo
  const handleLogoRemove = () => {
    setLogoPreview(null);
    handleChange('logo', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Logo supprimé');
  };

  const currencies = [
    { value: 'DA', label: 'Dinar Algérien (DA)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'USD', label: 'Dollar US ($)' }
  ];

  const languages = [
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' },
    { value: 'en', label: 'English' }
  ];

  const timezones = [
    { value: 'Africa/Algiers', label: 'Alger (UTC+1)' },
    { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
    { value: 'Europe/London', label: 'Londres (UTC+0)' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nom de la salle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <BuildingOfficeIcon className="h-4 w-4" />
            Nom de la salle
          </label>
          <input
            type="text"
            value={formData.gymName}
            onChange={(e) => handleChange('gymName', e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nom de votre salle de sport"
          />
        </motion.div>

        {/* Adresse */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <MapPinIcon className="h-4 w-4" />
            Adresse
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Adresse complète"
          />
        </motion.div>

        {/* Téléphone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <PhoneIcon className="h-4 w-4" />
            Téléphone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="+213 XXX XXX XXX"
          />
        </motion.div>

        {/* Email */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <EnvelopeIcon className="h-4 w-4" />
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="contact@gym.dz"
          />
        </motion.div>

        {/* Site web */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <GlobeAltIcon className="h-4 w-4" />
            Site web
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleChange('website', e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="https://votre-site.dz"
          />
        </motion.div>

        {/* Devise */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <CurrencyDollarIcon className="h-4 w-4" />
            Devise
          </label>
          <select
            value={formData.currency}
            onChange={(e) => handleChange('currency', e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {currencies.map(currency => (
              <option key={currency.value} value={currency.value} className="bg-gray-800 text-slate-900 dark:text-slate-50">
                {currency.label}
              </option>
            ))}
          </select>
        </motion.div>

        {/* Langue par défaut */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <LanguageIcon className="h-4 w-4" />
            Langue par défaut
          </label>
          <select
            value={formData.language}
            onChange={(e) => handleChange('language', e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {languages.map(lang => (
              <option key={lang.value} value={lang.value} className="bg-gray-800 text-slate-900 dark:text-slate-50">
                {lang.label}
              </option>
            ))}
          </select>
        </motion.div>

        {/* Fuseau horaire */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="space-y-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <ClockIcon className="h-4 w-4" />
            Fuseau horaire
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => handleChange('timezone', e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value} className="bg-gray-800 text-slate-900 dark:text-slate-50">
                {tz.label}
              </option>
            ))}
          </select>
        </motion.div>

        {/* Logo Upload */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="space-y-2 md:col-span-2"
        >
          <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm font-medium">
            <PhotoIcon className="h-4 w-4" />
            Logo de l'école
          </label>
          
          <div className="flex items-start gap-4">
            {/* Upload Area */}
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="flex flex-col items-center justify-center w-full h-32 bg-slate-100/80 dark:bg-slate-800/40 border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-xl cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
              >
                <div className="text-center">
                  {uploadStatus === 'uploading' ? (
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/60"></div>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">Téléchargement...</p>
                    </div>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-8 w-8 text-slate-400 dark:text-slate-500 mx-auto" />
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">Cliquez pour télécharger</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500">PNG, JPG (max. 5MB)</p>
                    </>
                  )}
                </div>
              </label>
              
              {uploadStatus === 'error' && (
                <p className="mt-2 text-sm text-red-400">
                  Erreur: Fichier invalide ou trop volumineux
                </p>
              )}
              
              {uploadStatus === 'success' && (
                <p className="mt-2 text-sm text-green-400">
                  Logo téléchargé avec succès
                </p>
              )}
            </div>

            {/* Logo Preview */}
            {logoPreview && (
              <div className="relative">
                <div className="w-32 h-32 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <button
                  onClick={handleLogoRemove}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-slate-900 dark:text-slate-50 rounded-full hover:bg-red-600 transition-colors"
                  title="Supprimer le logo"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Aperçu des paramètres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-lg rounded-2xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">Aperçu</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-600 dark:text-slate-400">Nom :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">{formData.gymName || 'Non défini'}</span>
          </div>
          <div>
            <span className="text-slate-600 dark:text-slate-400">Devise :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">{formData.currency}</span>
          </div>
          <div>
            <span className="text-slate-600 dark:text-slate-400">Langue :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">
              {languages.find(l => l.value === formData.language)?.label}
            </span>
          </div>
          <div>
            <span className="text-slate-600 dark:text-slate-400">Fuseau horaire :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">
              {timezones.find(t => t.value === formData.timezone)?.label}
            </span>
          </div>
          <div>
            <span className="text-slate-600 dark:text-slate-400">Logo :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">
              {formData.logo ? 'Configuré' : 'Non défini'}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default GeneralSettings; 